import { useEffect, type ReactNode } from 'react';
import {
  UnifiedThemeProvider,
  createUnifiedTheme,
  defaultTypography,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';
import { appThemeApiRef, useApi } from '@backstage/frontend-plugin-api';
import { ThemeBlueprint } from '@backstage/plugin-app-react';
import palette from './palette.json';
import { readThemeChoice } from './themeChoice';

const FONT_FAMILY = "RijksSans, 'NLDD Inter', system-ui, sans-serif";

// Backstage's own default font sits on each heading, not only on the base style.
const typography = {
  ...defaultTypography,
  fontFamily: FONT_FAMILY,
  ...Object.fromEntries(
    (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const).map(key => [
      key,
      { ...defaultTypography[key], fontFamily: FONT_FAMILY },
    ]),
  ),
};

const rijkPageTheme = genPageTheme({
  colors: [palette.light.primary, palette.light.primary],
  shape: shapes.wave,
});
const flatPageTheme = { ...rijkPageTheme, shape: 'none', backgroundImage: 'none' };
const pageTheme = Object.fromEntries(
  ['home', 'documentation', 'tool', 'service', 'website', 'library', 'other', 'app', 'apis'].map(
    key => [key, flatPageTheme],
  ),
);

// MUI computes alpha and hover tints, so it needs concrete colors. palette.json holds the
// design system's values and the contract test fails when they drift apart.
const paletteFrom = (colors: typeof palette.light) => ({
  primary: { main: colors.primary, contrastText: colors.contrast },
  secondary: { main: colors.accent },
  text: { primary: colors.text, secondary: colors.textSecondary },
  background: { default: colors.background, paper: colors.background },
  divider: colors.divider,
});

const componentOverrides = {
  MuiButton: {
    styleOverrides: {
      root: { textTransform: 'none' as const, borderRadius: 8 },
    },
  },
  MuiChip: { styleOverrides: { root: { borderRadius: 6 } } },
  MuiPaper: { styleOverrides: { rounded: { borderRadius: 12 } } },
  MuiTab: { styleOverrides: { root: { textTransform: 'none' as const } } },
  MuiTableCell: {
    styleOverrides: { head: { textTransform: 'none' as const } },
  },
};

const rijkLight = createUnifiedTheme({
  palette: {
    ...palettes.light,
    ...paletteFrom(palette.light),
    navigation: {
      ...palettes.light.navigation,
      background: palette.light.primary,
      indicator: palette.light.contrast,
    },
  },
  typography,
  defaultPageTheme: 'home',
  pageTheme,
  components: componentOverrides,
});

const rijkDark = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    ...paletteFrom(palette.dark),
    navigation: { ...palettes.dark.navigation, background: palette.dark.background },
  },
  typography,
  defaultPageTheme: 'home',
  pageTheme,
  components: componentOverrides,
});

/** Keeps the design system's light-dark() palette in step with the Backstage mode. */
function ColorScheme(props: { scheme: 'light' | 'dark'; children: ReactNode }) {
  const themeApi = useApi(appThemeApiRef);

  // A tab that stayed open follows a choice made elsewhere, as soon as it is back in use.
  useEffect(() => {
    const follow = () => {
      const choice = readThemeChoice();
      if (choice !== themeApi.getActiveThemeId()) {
        themeApi.setActiveThemeId(choice);
      }
    };
    window.addEventListener('focus', follow);
    window.addEventListener('pageshow', follow);
    return () => {
      window.removeEventListener('focus', follow);
      window.removeEventListener('pageshow', follow);
    };
  }, [themeApi]);

  useEffect(() => {
    document.documentElement.dataset.scheme = props.scheme;
  }, [props.scheme]);
  return <>{props.children}</>;
}

export const lightTheme = ThemeBlueprint.make({
  name: 'rijk-light',
  params: {
    theme: {
      id: 'rijk-light',
      title: 'Licht',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={rijkLight}>
          <ColorScheme scheme="light">{children}</ColorScheme>
        </UnifiedThemeProvider>
      ),
    },
  },
});

export const darkTheme = ThemeBlueprint.make({
  name: 'rijk-dark',
  params: {
    theme: {
      id: 'rijk-dark',
      title: 'Donker',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={rijkDark}>
          <ColorScheme scheme="dark">{children}</ColorScheme>
        </UnifiedThemeProvider>
      ),
    },
  },
});
